import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProfileCard from '../../components/ui/ProfileCard';
import ApiService from '../../services/apiService';
import { convertFirebaseTimestamp } from '../../utils/dateUtils';

const Dashboard = () => {
  const { currentUser, userProfile, isAdmin, fetchUserProfile } = useAuth();
  const navigate = useNavigate();
  const [learningSessions, setLearningSessions] = useState([]);
  const [stats, setStats] = useState([]);
  const [teachers, setTeachers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    console.log('Dashboard useEffect - userProfile:', userProfile);
    console.log('Dashboard useEffect - currentUser:', currentUser);
    console.log('Dashboard useEffect - hasFetchedData:', hasFetchedData.current);
    
    // Wait for user to be loaded
    if (!currentUser) {
      return;
    }

    // Redirect admins to admin dashboard
    if (userProfile && isAdmin()) {
      navigate('/admin');
      return;
    }

    // Redirect parents to parent dashboard
    if (userProfile && userProfile.role?.toLowerCase() === 'parent') {
      navigate('/parent/dashboard');
      return;
    }

    // Check if user needs to create a profile
    // Only redirect if we've confirmed there's no profile (not just loading)
    if (currentUser && userProfile === null) {
      console.log('No profile found, redirecting to create-profile');
      setLoading(false);
      navigate('/create-profile');
      return;
    }

    // Only fetch data if we have a profile and haven't fetched yet
    if (userProfile && !hasFetchedData.current) {
      console.log('Fetching dashboard data...');
      hasFetchedData.current = true;
      fetchDashboardData();
    }
  }, [currentUser, userProfile]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use existing userProfile from context (don't fetch again)
      if (!userProfile) {
        setLoading(false);
        return;
      }
      
      // Fetch learning sessions and teachers for the user
      const sessionsData = await fetchLearningSessions();
      await fetchTeachers();
      
      if (sessionsData) {
        // Calculate stats using userProfile from context
        const profileStats = calculateStats(userProfile, sessionsData || []);
        setStats([profileStats]);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLearningSessions = async () => {
    try {
      const token = await currentUser.getIdToken();
      const sessionsData = await ApiService.getUserLearningSessions(currentUser.uid, token);
      console.log("Sessions data: ", sessionsData);
      setLearningSessions(sessionsData.learningSessions || []);
      return sessionsData.learningSessions || [];
    } catch (error) {
      console.error('Error fetching sessions:', error);
      return [];
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/teachers`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch teachers');
      }

      const teachersData = await response.json();
      // Convert array to object with teacher ID as key for easy lookup
      const teachersMap = {};
      teachersData.forEach(teacher => {
        teachersMap[teacher.id] = teacher;
      });
      setTeachers(teachersMap);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers({});
    }
  };


  const calculateStats = (profile, sessions) => {
    const numberOfSessions = sessions.length;
    
    let averageLearningSessionTime = 0;
    let averageAttentionPercentage = 0;

    if (numberOfSessions > 0) {
      const validSessions = sessions.filter(
        session => session.time_minutes !== null && session.attention_percentage !== null
      );
      const validNumberOfSessions = validSessions.length;

      if (validNumberOfSessions > 0) {
        averageLearningSessionTime = Math.round(
          validSessions.reduce((acc, session) => acc + (session.time_minutes || 0), 0) / validNumberOfSessions
        );

        averageAttentionPercentage = Math.round(
          validSessions.reduce((acc, session) => acc + (session.attention_percentage || 0), 0) / validNumberOfSessions
        );
      }
    }

    return {
      id: profile.userId || currentUser.uid,
      name: profile.name || profile.firstName + ' ' + profile.lastName,
      type: profile.role || 'SOLO',
      numberOfSessions,
      averageLearningSessionTime,
      averageAttentionPercentage,
      summary: profile.summary || "No summary available yet",
      picture: profile.avatar || null,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-red-600 text-xl mb-4">{error}</p>
            <button
              onClick={() => navigate('/create-profile')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Profile
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Learning Dashboard</h1>
            <p className="text-gray-600 mt-2">Track your learning progress and start new sessions</p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/profiles')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Manage Profiles
            </button>
            <button
              onClick={() => navigate('/teachers')}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              Choose Teacher
            </button>
          </div>
        </div>

        {stats.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-md p-8 max-w-md mx-auto">
              <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Start Learning?</h3>
              <p className="text-gray-600 mb-6">Create your profile and begin your learning journey today!</p>
              <button
                onClick={() => navigate('/create-profile')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium"
              >
                Create Profile
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.map(stat => (
              stat.type !== 'PARENT' && (
                <ProfileCard key={stat.id} {...stat} />
              )
            ))}
          </div>
        )}

        {/* Recent Learning Sessions */}
        {learningSessions.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Learning Sessions</h2>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attention</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {learningSessions.map((session) => {
                      const teacher = teachers[session.teacherId];
                      const sessionDate = convertFirebaseTimestamp(session.createdAt);
                      
                      return (
                        <tr key={session.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sessionDate ? (
                              <div>
                                <div className="font-medium">
                                  {sessionDate.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {sessionDate.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </div>
                              </div>
                            ) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.time_minutes || 0} min
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.attention_percentage || 0}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {teacher ? (
                              <div className="flex items-center">
                                <div className="h-8 w-8 mr-3">
                                  {teacher.picture ? (
                                    <img
                                      className="h-8 w-8 rounded-full object-cover"
                                      src={teacher.picture}
                                      alt={teacher.name}
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                      <span className="text-xs font-bold text-white">
                                        {teacher.name.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{teacher.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {teacher.level} • {teacher.specialty}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">No Teacher</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/learning-session/${currentUser.uid}?session=${session.id}`)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;

